import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { ContactForm } from '../components/ContactForm'
import { useCMSCollection, useCMSSide, useVirksomhedsoplysninger } from '../hooks/useCMS'

const FALLBACK_PRINCIPLES = [
  {
    num: 1,
    title: 'For at hjælpe dig, må jeg starte hvor du er.',
    body: 'Du er ekspert på dig, og hvordan livet er for dig.\nDer er ikke en bestemt vej - der er kun din vej. Den finder vi sammen.',
  },
  {
    num: 2,
    title: 'Der er ikke noget man skal her i livet.',
    body: "Der er kun muligheder og valg.\nDine valg er dine, og 'Nej' er altid en mulighed.",
  },
  {
    num: 3,
    title: 'Forandringsprocessen kan ikke forceres.',
    body: 'Forandringen skal ske på en måde og i et tempo,\nsom passer til dig.',
  },
  {
    num: 4,
    title: 'For at give slip, må vi gribe fat.',
    body: 'At møde det som er gemt væk, kræver mod.\nMod er ikke fraværet af frygt.\nMod er at møde det der er svært, på trods af frygten.',
  },
  {
    num: 5,
    title: 'Tryghed er en forudsætning for healing.',
    body: 'Tillid er en forudsætning for tryghed.\nTillid opstår først når det som er svært,\nmødes med åbenhed, nærvær og omsorg.',
  },
  {
    num: 6,
    title: 'Krop, tanker og følelser påvirker hinanden indbyrdes,',
    body: 'Når den ene forandres, forandres de øvrige også.',
  },
  {
    num: 7,
    title: 'Kroppen har sin egen hukommelse.',
    body: 'Det som sindet ikke er parat til at møde, gemmes væk.',
  },
  {
    num: 8,
    title: 'Tanker skaber følelser.',
    body: 'Tankerne holder til i hovedet.\nFølelserne sidder i kroppen.',
  },
  {
    num: 9,
    title: 'INDTRYK, som ikke mødes af et modsvarende UDTRYK, sætter sig som TRYK i kroppen.',
    body: 'Spændinger opstår i kroppen,\nnår oplevelser ikke færdigbearbejdes af sindet.',
  },
]

export function GrundprincipperPage() {
  const { data: cmsPrinciples } = useCMSCollection('grundprincipper')
  const { side: cmsSide } = useCMSSide('mine-9-grundprincipper')
  const { info } = useVirksomhedsoplysninger()

  const principles = Array.isArray(cmsPrinciples) && cmsPrinciples.length > 0
    ? [...cmsPrinciples]
        .sort((a, b) => (a.nummer || 0) - (b.nummer || 0))
        .map((p) => ({
          num: p.nummer,
          title: p.titel,
          body: p.tekst ? p.tekst.replace(/<[^>]+>/g, '\n').replace(/\n\s*\n/g, '\n').trim() : '',
        }))
    : FALLBACK_PRINCIPLES

  useSEO({
    title: cmsSide?.seo_titel || 'Mine 9 grundprincipper | Trekantens Kropsterapi',
    description: cmsSide?.seo_beskrivelse || 'Jeg har 9 grund principper i min tilgang til at hjælpe mine klienter - Læs Bo Steendahls 9 grundprincipper.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/logo-11-dec.png',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Min tilgang &amp; filosofi
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'Mine 9 grundprincipper'}
          </h1>
          <p className="mt-4 text-lg sm:text-xl font-serif text-[#1b3a4b]/90 max-w-2xl mx-auto leading-relaxed">
            {cmsSide?.hero_underoverskrift || 'Jeg har 9 grund principper i min tilgang til at hjælpe mine klienter'}
          </p>
        </div>
      </section>

      {/* Principles List with Aside Contact Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-6">
              {principles.map((p) => (
                <div
                  key={p.num}
                  className="bg-white rounded-2xl p-7 sm:p-9 shadow-sm border border-[#E5E0D8] hover:border-[#1b3a4b]/40 transition-all flex flex-col sm:flex-row items-start gap-6 group"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#1b3a4b] text-[#FAF8F5] font-serif font-bold text-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#7c875a] transition-colors">
                    {p.num}
                  </div>
                  <div className="space-y-2 flex-1">
                    <h2 className="font-serif text-xl sm:text-2xl font-bold text-[#1b3a4b] leading-snug">
                      {p.title}
                    </h2>
                    <div className="text-sm sm:text-base text-[#475569] leading-relaxed whitespace-pre-line">
                      {p.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Aside Contact Form */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 space-y-6">
              <ContactForm />
              <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] text-xs text-[#475569] space-y-2.5 shadow-sm">
                <div className="font-semibold text-[#1b3a4b] text-sm">Direkte kontakt</div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#7c875a]" aria-hidden="true" />
                  <span>{info?.fullAddress || 'Vendersgade 37, 7000 Fredericia'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
                  <a href={`tel:${info?.phoneRaw || '53128213'}`} className="hover:text-[#1b3a4b] font-medium">
                    Tlf: {info?.phone || '53 12 82 13'}
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  )
}
